// @ts-nocheck
// src/components/gamification/TribeCard3D.tsx
// ReactBits-style 3D profile card for Tribes/Profile Groups
// Based on https://reactbits.dev/components/profile-card

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Doc } from '@/convex/_generated/dataModel';
import { Sparkles, Users, TrendingUp, ImageIcon, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GamificationBadge } from './GamificationBadge';
import { useTribeGamification } from '@/hooks/useTribeGamification';
import { useToast } from '@/components/ui/use-toast';
import { Id } from '@/convex/_generated/dataModel';
import '@/components/gamification/ProfileCard3D.css';

interface TribeCard3DProps {
	tribe: Doc<'profileGroups'>;
	canEdit?: boolean;
	userId?: string;
	className?: string;
	enableTilt?: boolean;
}

const ANIMATION_CONFIG = {
	SMOOTH_DURATION: 600,
	INITIAL_DURATION: 1500,
	INITIAL_X_OFFSET: 70,
	INITIAL_Y_OFFSET: 60,
} as const;

const TRIBE_CARD_GRADIENTS = {
	behind:
		"radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y), rgba(147, 51, 234, var(--card-opacity)) 4%, rgba(147, 51, 234, calc(var(--card-opacity)*0.75)) 10%, rgba(147, 51, 234, calc(var(--card-opacity)*0.5)) 50%, rgba(147, 51, 234, 0) 100%), radial-gradient(35% 52% at 55% 20%, #9333eac4 0%, #a855f700 100%), radial-gradient(100% 100% at 50% 50%, #a855f7ff 1%, #9333ea00 76%), conic-gradient(from 124deg at 50% 50%, #9333eaff 0%, #ec4899ff 40%, #ec4899ff 60%, #9333eaff 100%)",
	inner: "linear-gradient(145deg, #9333ea8c 0%, #ec489944 100%)",
} as const;

const clamp = (value: number, min = 0, max = 100): number =>
	Math.min(Math.max(value, min), max);

const round = (value: number, precision = 3): number =>
	parseFloat(value.toFixed(precision));

const adjust = (
	value: number,
	fromMin: number,
	fromMax: number,
	toMin: number,
	toMax: number
): number =>
	round(toMin + ((toMax - toMin) * (value - fromMin)) / (fromMax - fromMin));

const easeInOutCubic = (x: number): number =>
	x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

const formatNumber = (num: number): string => {
	if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
	if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
	return num.toString();
};

export function TribeCard3D({
	tribe,
	canEdit = false,
	userId,
	className = "",
	enableTilt = true,
}: TribeCard3DProps) {
	const { toast } = useToast();
	const wrapRef = useRef<HTMLDivElement>(null);
	const cardRef = useRef<HTMLDivElement>(null);
	
	const [isInitializing, setIsInitializing] = React.useState(false);
	const [isGeneratingArt, setIsGeneratingArt] = React.useState(false);
	const [isSharing, setIsSharing] = React.useState(false);

	const {
		analytics,
		isLoading,
		initializeGamification,
		generateArtwork,
		trackShare,
		tierConfig,
	} = useTribeGamification({
		tribeId: tribe._id,
		enabled: tribe.isGameEnabled,
	});

	const animationHandlers = useMemo(() => {
		if (!enableTilt) return null;

		let rafId: number | null = null;

		const updateCardTransform = (
			offsetX: number,
			offsetY: number,
			card: HTMLElement,
			wrap: HTMLElement
		) => {
			const width = card.clientWidth;
			const height = card.clientHeight;

			const percentX = clamp((100 / width) * offsetX);
			const percentY = clamp((100 / height) * offsetY);

			const centerX = percentX - 50;
			const centerY = percentY - 50;

			const properties = {
				"--pointer-x": `${percentX}%`,
				"--pointer-y": `${percentY}%`,
				"--background-x": `${adjust(percentX, 0, 100, 35, 65)}%`,
				"--background-y": `${adjust(percentY, 0, 100, 35, 65)}%`,
				"--pointer-from-center": `${clamp(Math.hypot(percentY - 50, percentX - 50) / 50, 0, 1)}`,
				"--pointer-from-top": `${percentY / 100}`,
				"--pointer-from-left": `${percentX / 100}`,
				"--rotate-x": `${round(-(centerX / 5))}deg`,
				"--rotate-y": `${round(centerY / 4)}deg`,
			};

			Object.entries(properties).forEach(([property, value]) => {
				wrap.style.setProperty(property, value);
			});
		};

		const createSmoothAnimation = (
			duration: number,
			startX: number,
			startY: number,
			card: HTMLElement,
			wrap: HTMLElement
		) => {
			const startTime = performance.now();
			const targetX = wrap.clientWidth / 2;
			const targetY = wrap.clientHeight / 2;

			const animationLoop = (currentTime: number) => {
				const elapsed = currentTime - startTime;
				const progress = clamp(elapsed / duration);
				const easedProgress = easeInOutCubic(progress);

				const currentX = adjust(easedProgress, 0, 1, startX, targetX);
				const currentY = adjust(easedProgress, 0, 1, startY, targetY);

				updateCardTransform(currentX, currentY, card, wrap);

				if (progress < 1) {
					rafId = requestAnimationFrame(animationLoop);
				}
			};

			rafId = requestAnimationFrame(animationLoop);
		};

		return {
			updateCardTransform,
			createSmoothAnimation,
			cancelAnimation: () => {
				if (rafId) {
					cancelAnimationFrame(rafId);
					rafId = null;
				}
			},
		};
	}, [enableTilt]);

	const handlePointerMove = useCallback(
		(event: PointerEvent) => {
			const card = cardRef.current;
			const wrap = wrapRef.current;

			if (!card || !wrap || !animationHandlers) return;

			const rect = card.getBoundingClientRect();
			animationHandlers.updateCardTransform(
				event.clientX - rect.left,
				event.clientY - rect.top,
				card,
				wrap
			);
		},
		[animationHandlers]
	);

	const handlePointerEnter = useCallback(() => {
		const card = cardRef.current;
		const wrap = wrapRef.current;

		if (!card || !wrap || !animationHandlers) return;

		animationHandlers.cancelAnimation();
		wrap.classList.add('pc-active');
		card.classList.add('pc-active');
	}, [animationHandlers]);

	const handlePointerLeave = useCallback(
		(event: PointerEvent) => {
			const card = cardRef.current;
			const wrap = wrapRef.current;

			if (!card || !wrap || !animationHandlers) return;

			animationHandlers.createSmoothAnimation(
				ANIMATION_CONFIG.SMOOTH_DURATION,
				event.offsetX,
				event.offsetY,
				card,
				wrap
			);
			wrap.classList.remove('pc-active');
			card.classList.remove('pc-active');
		},
		[animationHandlers]
	);

	useEffect(() => {
		if (!enableTilt || !animationHandlers) return;

		const card = cardRef.current;
		const wrap = wrapRef.current;

		if (!card || !wrap) return;

		const pointerMoveHandler = handlePointerMove as EventListener;
		const pointerEnterHandler = handlePointerEnter as EventListener;
		const pointerLeaveHandler = handlePointerLeave as EventListener;

		card.addEventListener("pointerenter", pointerEnterHandler);
		card.addEventListener("pointermove", pointerMoveHandler);
		card.addEventListener("pointerleave", pointerLeaveHandler);

		const initialX = wrap.clientWidth - ANIMATION_CONFIG.INITIAL_X_OFFSET;
		const initialY = ANIMATION_CONFIG.INITIAL_Y_OFFSET;

		animationHandlers.updateCardTransform(initialX, initialY, card, wrap);
		animationHandlers.createSmoothAnimation(
			ANIMATION_CONFIG.INITIAL_DURATION,
			initialX,
			initialY,
			card,
			wrap
		);

		return () => {
			card.removeEventListener("pointerenter", pointerEnterHandler);
			card.removeEventListener("pointermove", pointerMoveHandler);
			card.removeEventListener("pointerleave", pointerLeaveHandler);
			animationHandlers.cancelAnimation();
		};
	}, [
		enableTilt,
		animationHandlers,
		handlePointerMove,
		handlePointerEnter,
		handlePointerLeave,
	]);

	const handleEnableGamification = async () => {
		try {
			setIsInitializing(true);
			await initializeGamification({
				tribeId: tribe._id,
			});
			toast({
				title: '🎮 Social Game Joined!',
				description: 'This tribe is now participating in the game',
				duration: 3000,
			});
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to join the Social Game',
				variant: 'destructive',
			});
		} finally {
			setIsInitializing(false);
		}
	};

	const handleGenerateArtwork = async () => {
		try {
			setIsGeneratingArt(true);
			const result = await generateArtwork({
				tribeId: tribe._id,
				tribeName: tribe.name,
				description: tribe.description,
				regenerate: !!analytics?.artworkUrl,
			});

			if (result.success) {
				toast({
					title: '🎨 Artwork Generated!',
					description: 'Your tribe now has unique AI-generated artwork',
					duration: 3000,
				});
			}
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to generate artwork',
				variant: 'destructive',
			});
		} finally {
			setIsGeneratingArt(false);
		}
	};

	const handleShare = async () => {
		if (!userId) return;

		try {
			setIsSharing(true);
			const result = await trackShare({
				tribeId: tribe._id,
				sharedByUserId: userId as Id<'users'>,
			});

			toast({
				title: '🎉 Tribe Shared!',
				description: `Earned ${result.bonusPoints} bonus points!`,
				duration: 3000,
			});
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to share tribe',
				variant: 'destructive',
			});
		} finally {
			setIsSharing(false);
		}
	};

	const cardStyle = useMemo(
		() =>
			({
				"--behind-gradient": TRIBE_CARD_GRADIENTS.behind,
				"--inner-gradient": TRIBE_CARD_GRADIENTS.inner,
				"--card-opacity": 0.7,
			}) as React.CSSProperties,
		[]
	);

	return (
		<div
			ref={wrapRef}
			className={cn("pc-card-wrapper", className)}
			style={cardStyle}
		>
			<section ref={cardRef} className="pc-card">
				<div className="pc-inside">
					<div className="pc-shine" />
					<div className="pc-glare" />
					
					{/* Display tribe artwork WITHOUT opacity */}
					{tribe.isGameEnabled && analytics?.artworkUrl && (
						<div className="pc-content pc-avatar-content">
							<img
								className="pc-avatar"
								src={analytics.artworkUrl}
								alt={`${tribe.name} artwork`}
								loading="lazy"
								onError={(e) => {
									const target = e.target as HTMLImageElement;
									target.style.display = 'none';
								}}
							/>
						</div>
					)}
					
					<div className="pc-content">
						<div className="pc-details">
							<h3>{tribe.name}</h3>
							<p>{tribe.description || 'Social Media Tribe'}</p>
						</div>
					</div>

					{/* Tribe info overlay */}
					<div className="pc-user-info">
						{tribe.isGameEnabled && analytics ? (
							<>
								<div className="pc-user-details">
									<div className="pc-mini-avatar">
										<span className="text-2xl">{tierConfig.icon}</span>
									</div>
									<div className="pc-user-text">
										<div className="pc-handle">{tierConfig.label} Tier</div>
										<div className="pc-status">Active Game</div>
									</div>
								</div>

								{/* Metrics */}
								<div className="pc-metrics">
									<div className="pc-metric">
										<span className="pc-metric-value">{formatNumber(analytics.totalFollowers)}</span>
										<span className="pc-metric-label">Followers</span>
									</div>
									<div className="pc-metric">
										<span className="pc-metric-value">{analytics.activeMemberCount}/{analytics.memberCount}</span>
										<span className="pc-metric-label">Members</span>
									</div>
									<div className="pc-metric">
										<span className="pc-metric-value">{formatNumber(analytics.score)}</span>
										<span className="pc-metric-label">Score</span>
									</div>
								</div>

								{/* Action buttons - ALWAYS show Generate Artwork button */}
								<div className="flex gap-2 mb-3">
									{canEdit && (
										<Button
											onClick={handleGenerateArtwork}
											disabled={isGeneratingArt}
											size="sm"
											variant="outline"
											className="flex-1 pc-contact-btn"
											style={{ pointerEvents: "auto" }}
										>
											{isGeneratingArt ? (
												<>
													<div className='animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2' />
													Generating...
												</>
											) : (
												<>
													<ImageIcon className='h-3 w-3 mr-2' />
													{analytics.artworkUrl ? 'Update' : 'Generate'} Artwork
												</>
											)}
										</Button>
									)}

									{tribe.isCommunity && (
										<Button
											onClick={handleShare}
											disabled={isSharing}
											size="sm"
											variant="outline"
											className="flex-1 pc-contact-btn"
											style={{ pointerEvents: "auto" }}
										>
											{isSharing ? (
												<>
													<div className='animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2' />
													Sharing...
												</>
											) : (
												<>
													<Share2 className='h-3 w-3 mr-2' />
													Share
												</>
											)}
										</Button>
									)}
								</div>

								<GamificationBadge
									score={analytics.score}
									tier={analytics.tier || 'common'}
									growthRate={analytics.followersGrowthRate || 0}
									isLoading={isLoading}
									size='sm'
								/>
							</>
						) : canEdit ? (
							<div className="text-center py-4">
								<p className="text-sm text-gray-400 mb-3">
									Join the Social Game to track tribe growth
								</p>
								<button
									className="pc-contact-btn"
									onClick={handleEnableGamification}
									disabled={isInitializing}
									style={{ pointerEvents: "auto" }}
									type="button"
								>
									{isInitializing ? (
										<>
											<div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block' />
											Enabling...
										</>
									) : (
										<>
											<Sparkles className='h-4 w-4 mr-2 inline-block' />
											Join Social Game
										</>
									)}
								</button>
							</div>
						) : (
							<div className="text-center py-4">
								<p className="text-sm text-gray-400">
									{tribe.profileIds.length} profiles
								</p>
							</div>
						)}
					</div>
				</div>
			</section>
		</div>
	);
}
